import React, { useState, useEffect } from 'react';
import { BlockMetricsTable } from '../components/BlockPerformance';
import { blockPerformanceService, BlockPerformanceResponse } from '../services/blockPerformanceService';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { MetricCard } from '../components/ui/MetricCard';

export const BlockDetails: React.FC = () => {
  const [data, setData] = useState<BlockPerformanceResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedPlant, setSelectedPlant] = useState('');

  useEffect(() => {
    fetchBlockData();
  }, [selectedDate, selectedPlant]);

  const fetchBlockData = async () => {
    try {
      setLoading(true);
      const response = await blockPerformanceService.getBlockPerformance(
        selectedDate, 
        selectedPlant || undefined
      );
      setData(response);
    } catch (error) {
      console.error('Error fetching block data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = (format: 'csv' | 'pdf') => {
    if (!data) return;
    
    if (format === 'csv') {
      const csvContent = [
        ['Block Name', 'DC CUF (%)', 'AC CUF (%)', 'PR (%)', 'Daily Yield', 'Inverter Gen', 'Meter Gen', 'Sync Status'],
        ...data.blocks.map(block => [
          block.block_name,
          block.dc_cuf,
          block.ac_cuf,
          block.pr,
          block.daily_yield,
          block.inverter_generation,
          block.meter_generation,
          block.sync_status ? 'OK' : 'Issue'
        ])
      ].map(row => row.join(',')).join('\n');
      
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `block-performance-${selectedDate}.csv`;
      a.click();
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">Block Details - Performance Diagnostics</h1>
        
        <div className="flex gap-4">
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="px-3 py-2 border rounded-md"
          />
          <select
            value={selectedPlant}
            onChange={(e) => setSelectedPlant(e.target.value)}
            className="px-3 py-2 border rounded-md"
          >
            <option value="">All Plants</option>
            <option value="1">PSPR_Hindupur</option>
          </select>
        </div>
      </div>

      {data && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <MetricCard
              title="Total Blocks"
              value={data.summary.total_blocks}
              unit=""
              trend="neutral"
            />
            <MetricCard
              title="Avg DC CUF"
              value={Number(data.summary.avg_dc_cuf).toFixed(1)}
              unit="%"
              trend={data.summary.avg_dc_cuf >= 80 ? 'up' : 'down'}
            />
            <MetricCard
              title="Avg PR"
              value={Number(data.summary.avg_pr).toFixed(1)}
              unit="%"
              trend={data.summary.avg_pr >= 85 ? 'up' : 'down'}
            />
            <MetricCard
              title="Sync Issues"
              value={data.summary.sync_issues}
              unit=""
              trend={data.summary.sync_issues === 0 ? 'up' : 'down'}
            />
          </div>

          <BlockMetricsTable 
            blocks={data.blocks} 
            onExport={handleExport}
          />
        </>
      )}
    </div>
  );
};